import Vue from 'vue';
import { dayjs } from '@/plugins/dayjs/install';
import {
  matchesInitialState,
  filterInitialState
} from '@/store/modules/matches';

function setMatches(state, { matches }) {
  state.matches.active = matches;
}

function setLoadingMatches(state, { isLoadingMatches }) {
  state.isLoadingMatches = isLoadingMatches;
}

function setLoadingStatusCounts(state, { isLoadingStatusCounts }) {
  state.isLoadingStatusCounts = isLoadingStatusCounts;
}

function setActiveTab(state, activeTab) {
  state.activeTab = activeTab;
}

function setInitialMatchStatusCounts(state, counts) {
  state.initialMatchStatusCounts = counts;
}

function setMatchStatusCounts(state, counts) {
  state.matchStatusCounts = counts;
}

function updateLastMessage(state, data) {
  const allMatches = Object.values(state.matches);
  allMatches.forEach(matchesInTab => {
    if (!Array.isArray(matchesInTab)) return;

    const matchIndex = matchesInTab.findIndex(
      match => match.chatRoomId == data.chatRoomId
    );
    if (matchIndex === -1) return;

    const currentMatch = matchesInTab[matchIndex];

    const conversation = currentMatch.chatRoom?.conversations.find(
      conversation => conversation.ownerId === data.senderId
    );
    const address = conversation ? conversation.owner.address : 'SYSTEM';

    Vue.set(currentMatch, 'lastMessage', {
      message: data.content,
      senderName: address,
      sentAt: data.createdAt
    });

    // Add unread count if the sender is not the current user
    if (
      currentMatch.chatRoom &&
      currentMatch.chatRoom.conversations[0].ownerId !== data.senderId &&
      data.senderId !== -1
    ) {
      Vue.set(currentMatch, 'unreadCount', currentMatch.unreadCount + 1);
    }

    if (
      currentMatch.isUnanswered &&
      currentMatch.chatRoom &&
      currentMatch.chatRoom.conversations[0].ownerId === data.senderId
    ) {
      Vue.set(currentMatch, 'isUnanswered', false);
    }
  });
}

// The card renders lastMessage with v-html; socket content is raw user
// input, so escape it before it can reach that sink.
function escapeHtml(value) {
  return String(value).replace(
    /[&<>"']/g,
    ch =>
      ({
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#39;'
      }[ch])
  );
}

// The requests list keeps chat rooms (not matches), so the match-list
// updateLastMessage above never reaches it — without this the preview on
// a request card stayed on the previous message until a hard reload.
// Mirrors the inbox (Chat/Index.vue handleNewMessage) and the match-list
// unread convention above: a message from the other party also bumps the
// unread counts (the card's isUnread() reads the conversation unreadCount,
// so without the bump a new incoming message renders as already read).
// SYSTEM payloads (senderId -1) are skipped since the card renders
// lastMessage as-is.
function updateMatchRequestLastMessage(state, data) {
  const rooms = state.matchRequests?.rooms;
  if (!rooms || !data || data.senderId == -1) return;

  const index = rooms.findIndex(
    room => String(room.id) === String(data.chatRoomId)
  );
  if (index === -1) return;

  const room = rooms[index];
  const fromOtherParty =
    room.conversations?.[0] && room.conversations[0].ownerId !== data.senderId;

  const conversations = fromOtherParty
    ? room.conversations.map(conversation =>
        String(conversation.chatRoomId) === String(data.chatRoomId)
          ? {
              ...conversation,
              unreadCount: Number(conversation.unreadCount || 0) + 1
            }
          : conversation
      )
    : room.conversations;

  Vue.set(rooms, index, {
    ...room,
    conversations,
    unreadCount: fromOtherParty
      ? Number(room.unreadCount || 0) + 1
      : room.unreadCount,
    lastMessage: escapeHtml(data.content),
    updatedAt: dayjs.tz().format()
  });
}

// Only v1 matches live in these tab lists — the views route request and
// v2-node reads to their own reset paths; tabs without the match are skipped.
function resetReadCount(state, selectedMatch) {
  if (selectedMatch?.id == null) return;

  Object.values(state.matches).forEach(matchesInTab => {
    if (!Array.isArray(matchesInTab)) return;

    const match = matchesInTab.find(
      m => String(m.id) === String(selectedMatch.id)
    );
    if (match) Vue.set(match, 'unreadCount', 0);
  });
}

function resetMatchRequestReadCount(state, selectedRequest) {
  const allLists = Object.values(state.matchRequests || {});
  if (!allLists.length) return;

  allLists.forEach(list => {
    if (!Array.isArray(list)) return;

    const idx = list.findIndex(
      r => String(r.id) === String(selectedRequest.id)
    );
    if (idx === -1) return;

    const req = list[idx];

    const updated = {
      ...req,
      unreadCount: 0,
      conversations: (req.conversations || []).map(c => ({
        ...c,
        unreadCount: 0
      }))
    };

    if (Vue && Vue.set) {
      Vue.set(list, idx, updated);
    } else {
      list.splice(idx, 1, updated);
    }
  });
}

function appendMatches(state, { matches }) {
  state.matches.active = [...(state.matches.active || []), ...matches];
}

function setMatchesHasMore(state, hasMore) {
  state.matchesHasMore = hasMore;
}

function resetMatches(state) {
  state.matches = { ...matchesInitialState };
  state.matchesHasMore = true;
}

function setNewMatch(state, match) {
  state.newMatch = match;
}

// A room holding only the user's own conversation (the other ad is gone) has
// no counterpart for MatchRequestCard to render (KL-T).
function withCounterpart(rooms) {
  return (rooms || []).filter(room => room?.conversations?.length >= 2);
}

function setMatchRequests(state, rooms) {
  if (!state.matchRequests) state.matchRequests = { rooms: [], hasMore: true };
  state.matchRequests.rooms = withCounterpart(rooms);
}

// How many rooms the API has returned so far, including those withCounterpart
// leaves out: the next page starts after all of them.
function setMatchRequestsFetchedCount(state, fetchedCount) {
  if (!state.matchRequests) state.matchRequests = { rooms: [], hasMore: true };
  Vue.set(state.matchRequests, 'fetchedCount', fetchedCount);
}

function appendMatchRequests(state, rooms) {
  if (!state.matchRequests) state.matchRequests = { rooms: [], hasMore: true };
  state.matchRequests.rooms = [
    ...state.matchRequests.rooms,
    ...withCounterpart(rooms)
  ];
}
function setHasMore(state, hasMore) {
  if (!state.matchRequests) state.matchRequests = { rooms: [], hasMore: true };
  state.matchRequests.hasMore = hasMore;
}

function setLoadingMatchRequests(state, { isLoadingMatchRequests }) {
  state.isLoadingMatchRequests = isLoadingMatchRequests;
}

function removeMatchRequest(state, requestId) {
  if (!state.matchRequests || !state.matchRequests.rooms) return;
  const index = state.matchRequests.rooms.findIndex(x => x.id == requestId);
  if (index !== -1) {
    state.matchRequests.rooms.splice(index, 1);
    // Gone on the server too, so its later rooms move up one place.
    if (state.matchRequests.fetchedCount > 0 && requestId != 0) {
      state.matchRequests.fetchedCount -= 1;
    }
  }
}

function setFilterValue(state, filterValue) {
  state.filter = { ...state.filter, ...filterValue };
}

function resetFilter(state) {
  state.filter = { ...filterInitialState };
}

function setActiveToggleTab(state, activeToggleTab) {
  state.activeToggleTab = activeToggleTab;
}

function markMatchAsAnswered(state, matchId) {
  const allMatches = Object.values(state.matches);
  allMatches.forEach(matchesInTab => {
    if (!Array.isArray(matchesInTab)) return;

    const match = matchesInTab.find(m => m.id === matchId);
    if (match) {
      Vue.set(match, 'isUnanswered', false);
    }
  });
}

export default {
  setMatches,
  setLoadingMatches,
  setLoadingStatusCounts,
  setActiveTab,
  setInitialMatchStatusCounts,
  setMatchStatusCounts,
  updateLastMessage,
  updateMatchRequestLastMessage,
  resetReadCount,
  resetMatchRequestReadCount,
  appendMatches,
  setMatchesHasMore,
  resetMatches,
  setNewMatch,
  setMatchRequests,
  setMatchRequestsFetchedCount,
  appendMatchRequests,
  setHasMore,
  setLoadingMatchRequests,
  removeMatchRequest,
  setFilterValue,
  resetFilter,
  setActiveToggleTab,
  markMatchAsAnswered
};
